import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { moment } from '@seiue/moment'
import { Button, Modal, Toast } from '@seiue/ui'
import { debounce, isEmpty } from '@seiue/util'
import { Form } from 'antd'
import { FC, useEffect, useMemo, useRef, useState } from 'react'

import {
  FieldDomainEnum,
  FieldTypeEnum,
  TaskReq,
  TaskStatusEnum,
  TaskTypeEnum,
  activityApi,
  questionnaireApi,
} from 'packages/sdks-next/growp'

import { CommonBasicInfoRef } from '@/components/CommonBasicInfo'
import { ContentCard } from '@/components/ContentCard'
import { ModalLayout } from '@/components/ModalLayout'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'
import {
  QuestionnaireForward,
  ReqWithMetaData,
  questionnaireDataFormat,
} from '@/uikit/Questionnaire'
import { getTheme } from '@/utils/theme'

import { EditTaskModalProps } from '../types'

import { BasicInfo } from './BasicInfo'

/**
 * 编辑问卷任务弹窗
 *
 * @param param0 - 参数
 * @param param0.onClose - 关闭弹窗
 * @param param0.taskId - 任务 ID
 * @param param0.activityId - 活动 ID
 * @param param0.activityTaskOptions - 活动任务选项
 * @param param0.domain - 领域
 * @returns 组件
 */
export const EditQuestionnaireTaskModal: FC<EditTaskModalProps> = ({
  onClose,
  activityId,
  taskId,
  activityTaskOptions,
  domain,
}) => {
  const theme = getTheme()
  const [basicInfoForm] = Form.useForm()
  const [questionnaires, setQuestionnaires] = useState<ReqWithMetaData[]>([])
  const [task, setTask] = useState<TaskReq>()

  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [currentStep, setCurrentStep] = useState<number>(0)
  const basicInfoRef = useRef<CommonBasicInfoRef>(null)
  const questionnaireRef = useRef<{
    questionnaires: ReqWithMetaData
    task: TaskReq
  }>(null)
  const [structIsChange, setStructIsChange] = useState<boolean>(false)

  const isEdit = !!taskId

  const { data: questionnaire } =
    questionnaireApi.useLoadQuestionnaireActivityTask(taskId || 0, {
      disable: !taskId,
    })

  const { api: createQuestionnaireActivityTask } =
    questionnaireApi.useCreateQuestionnaireActivityTask({
      reload: [
        questionnaireApi.loadQuestionnaireActivityTask,
        activityApi.queryTasks,
        activityApi.loadActivity,
      ],
    })
  const { api: updateQuestionnaireActivityTask } =
    questionnaireApi.useUpdateQuestionnaireActivityTask({
      reload: [
        questionnaireApi.loadQuestionnaireActivityTask,
        activityApi.queryTasks,
        activityApi.loadActivity,
      ],
    })

  const commonBasicInfo = useMemo(() => {
    const {
      startAt: apiResStartAt,
      endAt: apiResEndAt,
      adminIds: apiResAdminIds,
      timedStartAt: apiResTimedStartAt,
      prevTaskIds: apiResPrevTaskIds,
      isShow,
    } = questionnaire?.task || {}
    const { startAt, endAt, adminIds, timedStartAt, prevTaskIds } =
      questionnaireRef.current?.task || {}
    return {
      endAt: endAt || apiResEndAt,
      adminIds: adminIds || apiResAdminIds,
      timedStartAt: timedStartAt || apiResTimedStartAt,
      prevTaskIds: prevTaskIds || apiResPrevTaskIds,
      startAt: isEdit
        ? startAt || apiResStartAt
        : moment().format('YYYY-MM-DD HH:mm:ss'),
      isShow: isShow !== undefined ? isShow : true,
    }
  }, [questionnaire?.task, currentStep])

  const getQuestionnaireBaseInfo = () => {
    const { name, description: apiDescription } = questionnaire?.task || {}
    return {
      name: title || name,
      description: description || apiDescription,
    }
  }

  useEffect(() => {
    if (currentStep === 0) {
      const baseInfo = getQuestionnaireBaseInfo()
      basicInfoForm.setFieldsValue({ ...baseInfo })
    }
  }, [currentStep, questionnaire?.task])

  const getQuestionnaireTaskInfo = () => {
    return {
      ...basicInfoForm.getFieldsValue(),
      ...basicInfoRef.current?.getBasicInfo(),
    }
  }

  const questionList = questionnaireDataFormat(
    questionnaire?.questionnaire.fields || []
  )

  const filterTaskOptions =
    activityTaskOptions?.filter((o) => o.value !== taskId) || []

  useEffect(() => {
    setQuestionnaires([...questionList])
    setTask(questionnaire?.task)
  }, [questionnaire])

  const beforeUpdateInfo = async (status: TaskStatusEnum) => {
    if (currentStep === 0) {
      const commonRefInfo = basicInfoRef.current?.getBasicInfo()
      if (isEmpty(commonRefInfo)) {
        return
      }
    }

    if (isEdit && structIsChange) {
      // 编辑时修改了问卷结构 检查是否已经有用户提交过数据
      const { data } = await questionnaireApi.checkQuestionnaireTaskIsSubmit(
        taskId
      )
      if (data) {
        Modal.confirm({
          title: '确定保存修改吗？',
          content: `已有 ${data} 位成员提交了此问卷；保存修改并发布活动后，成员需重新完成此任务，确认要继续吗？`,
          onOk: () => {
            updateInfo(status, structIsChange)
          },
          okButtonProps: {
            style: {
              backgroundColor: theme.brand._1,
              borderColor: theme.brand._1,
            },
          },
        })
      } else {
        updateInfo(status, structIsChange)
      }
    } else {
      updateInfo(status, structIsChange)
    }
  }

  const updateInfo = async (
    status: TaskStatusEnum,
    isUpdateFields: boolean
  ) => {
    const questionCount = questionnaires.filter(
      (q) => ![FieldTypeEnum.Describe, FieldTypeEnum.PageBreak].includes(q.type)
    ).length
    if (questionCount < 1) {
      Toast.warning('至少设置一道题目')
      return
    }
    const finalTask = {
      ...(currentStep === 0
        ? getQuestionnaireTaskInfo()
        : questionnaireRef.current?.task),
      status,
      activityId,
      type: TaskTypeEnum.Questionnaire,
    }
    // 过滤掉未设置文案的描述
    const finalQuestionnaires = questionnaires.filter(
      (i) => !(i.type === FieldTypeEnum.Describe && !i.label)
    )
    const payload = {
      task: finalTask,
      questionnaires: finalQuestionnaires,
      domain: domain || FieldDomainEnum.InformalStudent,
      isUpdateFields,
    }
    if (taskId) {
      await updateQuestionnaireActivityTask({ id: taskId, payload })
      Toast.success(isEdit ? '更新成功' : '保存成功')
      onClose()
    } else {
      await createQuestionnaireActivityTask({
        ...payload,
      })
      Toast.success(isEdit ? '更新成功' : '保存成功')
      onClose()
    }
  }

  const debounceUpdateInfo = debounce(beforeUpdateInfo, 1000, { leading: true })

  return (
    <ModalLayout
      title={
        <TaskHeaderLeft
          title={title || questionnaire?.task.name || '未命名问卷'}
          onClose={() => {
            Modal.confirm({
              title: '确定退出问卷编辑吗？',
              content: '退出后后当前修改不会被保存，是否确定退出？',
              onOk: () => {
                onClose()
              },
              okButtonProps: {
                style: {
                  backgroundColor: theme.brand._1,
                  borderColor: theme.brand._1,
                },
              },
            })
          }}
        />
      }
      headerRight={
        <>
          <Button
            type="primary"
            onClick={() => debounceUpdateInfo(TaskStatusEnum.Published)}
          >
            {isEdit ? '更新' : '确认'}
          </Button>
        </>
      }
      stepsParams={{
        size: 'small',
        current: currentStep,
        items: [{ title: '基本信息' }, { title: '设置问卷' }],
        onChange: () => {
          if (currentStep === 0) {
            const commonRefInfo = basicInfoRef.current?.getBasicInfo()
            if (isEmpty(commonRefInfo)) {
              return
            }
            basicInfoForm.validateFields().then(() => {
              setTask(getQuestionnaireTaskInfo())
              setCurrentStep(1)
            })
          } else {
            setCurrentStep(0)
          }
        },
      }}
      contentStyle={{
        padding: '0 0',
      }}
      content={
        <>
          {currentStep === 0 ? (
            <ContentCard
              style={{ minHeight: 200, marginTop: '20px' }}
              contentStyle={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <BasicInfo
                setTitle={setTitle}
                setDescription={setDescription}
                form={basicInfoForm}
                commonBasicInfo={commonBasicInfo}
                basicInfoRef={basicInfoRef}
                activityTaskOptions={filterTaskOptions}
                isSubmit={questionnaire?.isSubmit}
              />
            </ContentCard>
          ) : task ? (
            <QuestionnaireForward
              questionnaires={questionnaires}
              setQuestionnaires={(v) => setQuestionnaires(v)}
              ref={questionnaireRef}
              domain={domain || FieldDomainEnum.InformalStudent}
              task={task}
              goBack={() => {
                setCurrentStep(0)
              }}
              structIsChange={
                isEdit ? () => setStructIsChange(true) : undefined
              }
            />
          ) : null}
          {currentStep === 1 ? null : (
            <ContentCard style={{ textAlign: 'center', marginBottom: 20 }}>
              <Button
                iconRight={currentStep === 0 ? faArrowRight : undefined}
                icon={currentStep === 1 ? faArrowLeft : undefined}
                onClick={async () => {
                  if (currentStep === 0) {
                    const commonRefInfo = basicInfoRef.current?.getBasicInfo()
                    if (isEmpty(commonRefInfo)) {
                      return
                    }
                    basicInfoForm.validateFields().then(() => {
                      setTask(getQuestionnaireTaskInfo())
                      setCurrentStep(1)
                    })
                  } else {
                    setCurrentStep(0)
                  }
                }}
              >
                {currentStep === 0 ? '下一步' : '上一步'}
              </Button>
            </ContentCard>
          )}
        </>
      }
    />
  )
}
